<template>
  <div class="w-full max-w-[810px] mx-auto mt-32 lg:mt-[88px] px-16">
    <div class="flex mb-16">
      <h1 class="text-h2 grow">Jobs</h1>
      <div v-if="loading" class="w-72 h-32 bg-grey-divider-subdued animate-pulse"></div>
      <Button v-else data-testid="new-job-button" variant="text" class="cursor-pointer" @click="newJobClick">New Job</Button>
    </div>
    <Tabs
      v-model="tabSelectionIndex"
      :tabs="[
        { title: 'Open', parenthesesNumber: openJobsCount },
        { title: 'Closed', parenthesesNumber: closedJobsCount },
      ]"
      class="mb-24"
    />
    <router-view />
    <ActionsPopup v-model="showJobPostPopup" title="What type of job?" :buttons="jobPostButtons" />
    <ActionsPopup v-model="showUpgradePopup" title="Upgrade to post more jobs" :buttons="upgradeButtons">
      <p class="text-body">Either close your open job or upgrade to post more jobs</p>
    </ActionsPopup>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import Tabs from '@/components/tabs/Tabs.vue';
import Button from '@/components/button/Button.vue';
import ActionsPopup from '@/components/actionsPopup/ActionsPopup.vue';
import type { ActionButton } from '@/components/actionsPopup/types';
import { FETCH_JOB_COUNTS } from '@/graphql/account/queries';

export default defineComponent({
  name: 'SeekerJobs',
  components: {
    Tabs,
    Button,
    ActionsPopup,
  },
  apollo: {
    jobCounts: {
      query: FETCH_JOB_COUNTS,
      update(data) {
        return data.viewer || null;
      },
      result() {
        this.loading = false;
      },
    },
  },
  data(): {
    tabSelectionIndex: number;
    jobCounts: any;
    loading: boolean;
    showJobPostPopup: boolean;
    showUpgradePopup: boolean;
  } {
    return {
      tabSelectionIndex: 0,
      jobCounts: null,
      loading: true,
      showJobPostPopup: false,
      showUpgradePopup: false,
    };
  },
  computed: {
    ...mapGetters('account', { account: 'getAccount' }),
    openJobsCount(): number | null {
      return this.jobCounts?.openJobs?.totalCount ?? null;
    },
    closedJobsCount(): number | null {
      return this.jobCounts?.closedJobs?.totalCount ?? null;
    },
    upgradeToPost(): boolean {
      // Show upgrade button if user is basic and has at least one open job
      return !!(this.account?.isBasic() && (this.openJobsCount ?? 0) > 0);
    },
    jobPostButtons(): ActionButton[] {
      const buttons: ActionButton[] = [
        { id: 'child-care', value: 'Child Care', to: '/job-post', variant: 'outlined' },
        { id: 'pet-care', value: 'Pet Care', href: '/job_posting/new?care_type=pet_care', variant: 'outlined' },
        { id: 'companion-care', value: 'Companion Care', href: '/job_posting/new?care_type=companion_care', variant: 'outlined' },
        {
          id: 'special-needs',
          value: 'Special Needs Care',
          href: '/job_posting/new?care_type=special_needs',
          variant: 'outlined',
        },
      ];
      if (this.account?.isCorporate()) {
        buttons.splice(1, 0, {
          id: 'housekeeping',
          value: 'Housekeeping',
          href: '/job_posting/new?care_type=housekeeping',
          variant: 'outlined',
        });
      }
      return buttons;
    },
    upgradeButtons(): ActionButton[] {
      return [
        {
          id: 'premium-upgrade-button',
          value: 'Upgrade to premium',
          to: '/upgrade/pricing',
          variant: 'primary',
        },
      ];
    },
  },
  watch: {
    $route: {
      handler(route: { name?: string }) {
        const routeToIndexMap: Record<string, number> = {
          'seeker-jobs-open': 0,
          'seeker-jobs-closed': 1,
        };
        const index = route.name ? routeToIndexMap[route.name] : undefined;
        if (index !== undefined && this.tabSelectionIndex !== index) {
          this.tabSelectionIndex = index;
        }
      },
      immediate: true,
    },
    tabSelectionIndex(newIndex: number): void {
      const routeNames = ['seeker-jobs-open', 'seeker-jobs-closed'];
      const routeName = routeNames[newIndex];
      if (routeName && this.$route.name !== routeName) {
        this.$router.push({ name: routeName });
      }
    },
  },
  methods: {
    newJobClick(): void {
      if (this.upgradeToPost) this.showUpgradePopup = true;
      else this.showJobPostPopup = true;
    },
  },
});
</script>
